import { IonAvatar, IonText } from '@ionic/react';
import { useInstance } from '../support/ioc/useInstance';
import { useSubscription } from '@autopylot-internal/state';
import { Button } from '../components/button/Button.tsx';
import { TrackingService } from '../support/tracking/TrackingService.ts';
import { HistoryViewModel } from '../app/HistoryViewModel.ts';
import { OperatorState } from '../profile/OperatorState.ts';
import { AuthService } from '../auth/AuthService.ts';
import { MenuViewModel } from './MenuViewModel.ts';
import { ICertificationType } from '../profile/IOperator.ts';
import { useBreakpoints } from '../support/hooks/useBreakpoints.tsx';
import { DeviceStatusBar } from '../support/DeviceStatusBar.ts';

interface IHeadingsProps {
    name?: string;
    email?: string;
    certificationType?: ICertificationType;
    isGuest: boolean;
}

const Headings: React.FC<IHeadingsProps> = ({ name, email, certificationType, isGuest }) => {
    const tracker = useInstance(TrackingService);
    const authService = useInstance(AuthService);

    const onCreateAccount = async () => {
        tracker.track(`Nav | Create Account or Sign In`);
        await authService.showLoginDialog({ message: 'Sign up for free, no password required!' });
    };

    return (
        <div style={{display: 'flex', flexDirection: 'column'}}>
            <IonText style={{fontSize: 18, fontWeight: 'bold'}}>{name || (isGuest ? 'Guest' : 'Pilot')}</IonText>
            {isGuest &&
                <div style={{margin: '-12px -19px -10px'}}>
                    <Button fill="clear" onClick={onCreateAccount}>Create Account or Sign In</Button>
                </div>
            }
            {!isGuest &&
                <div style={{width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingTop: 6, paddingBottom: 8}}>
                    <IonText style={{overflowWrap: 'anywhere'}} color="medium">
                        {email}
                    </IonText>
                </div>
            }
            <IonText data-id="menu-cert-type" style={{fontSize: 16, fontWeight: 'bold'}}>{certificationType === 'part-107' ? 'Part 107' : 'Recreational'}</IonText>
        </div>
    );
};

export const MenuHeader: React.FC = () => {
    const history = useInstance(HistoryViewModel);

    const { isLargerScreen } = useBreakpoints();

    const operatorState = useInstance(OperatorState);
    useSubscription(() => operatorState);

    const menuVM = useInstance(MenuViewModel);

    const statusBar = useInstance(DeviceStatusBar);

    const onClickAvatar = operatorState.isGuest() ? undefined : menuVM.clickHandler(() => {
        history.push('/profile');
    });

    // on desktop, show avatar and left-align headings
    if (isLargerScreen) {
        return (
            <div style={{display: 'flex', alignItems: 'flex-start', gap: 10, padding: '40px 10px 30px'}}>
                <IonAvatar title="Profile" style={{cursor: 'pointer', width: 44, height: 44, border: '2px solid white', background: 'white', filter: 'drop-shadow(0px 3px 5px var(--ion-color-step-200)'}} onClick={onClickAvatar}>
                    <img style={{objectFit: 'contain', width: 40, maxWidth: 40}} src={operatorState.state.pictureUrl || './assets/icon/icon.png'} referrerPolicy="no-referrer" alt="avatar"/>
                </IonAvatar>
                <Headings
                    name={operatorState.getName()}
                    email={operatorState.getEmail()}
                    certificationType={operatorState.state.certificationType}
                    isGuest={operatorState.isGuest()}
                />
            </div>
        );
    }

    // on mobile, no avatar and center-align headings
    return (
        <div style={{textAlign: 'center', padding: `calc(4vh + ${statusBar.getHeight()}px) 10px 4vh`}}>
            <Headings
                name={operatorState.getName()}
                email={operatorState.getEmail()}
                certificationType={operatorState.state.certificationType}
                isGuest={operatorState.isGuest()}
            />
        </div>
    );
};
